import { E2EE_ALG, E2EE_KEY_BYTES } from './constants';
import { base64UrlDecode, base64UrlEncode } from './encoding';
import { isE2eeKeyRecord, type E2eeAlgorithm, type E2eeKeyRecord } from './types';

const E2EE_PEM_LABEL = 'BUILD AUTOMATON E2EE KEY';

function pemHeaderValue(headers: Record<string, string>, name: string): string | undefined {
  return headers[name.toLowerCase()];
}

function encodePemBase64(bytes: Uint8Array): string {
  const raw = base64UrlEncode(bytes).replace(/-/g, '+').replace(/_/g, '/');
  const padded = raw + '='.repeat((4 - (raw.length % 4)) % 4);
  return padded.replace(/.{1,64}/g, '$&\n').trimEnd();
}

function decodePemBase64(value: string): Uint8Array {
  const compact = value.replace(/\s+/g, '');
  const binary = atob(compact);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

function parseE2eeKeyRecordPem(raw: string, fallbackName: string): E2eeKeyRecord {
  const lines = raw.trim().split(/\r?\n/);
  if (lines[0]?.trim() !== `-----BEGIN ${E2EE_PEM_LABEL}-----`) {
    throw new Error('The E2EE key file is not a BuildAutomaton PEM key.');
  }
  const endIndex = lines.findIndex((line) => line.trim() === `-----END ${E2EE_PEM_LABEL}-----`);
  if (endIndex < 0) throw new Error('The E2EE key PEM is missing its end marker.');

  const headers: Record<string, string> = {};
  const bodyLines: string[] = [];
  let readingBody = false;
  for (const line of lines.slice(1, endIndex)) {
    const trimmed = line.trim();
    if (!trimmed) {
      readingBody = true;
      continue;
    }
    const headerMatch = /^([A-Za-z0-9-]+):\s*(.*)$/.exec(trimmed);
    if (!readingBody && headerMatch) {
      headers[headerMatch[1].toLowerCase()] = headerMatch[2].trim();
      continue;
    }
    readingBody = true;
    bodyLines.push(trimmed);
  }

  const keyBytes = decodePemBase64(bodyLines.join(''));
  if (keyBytes.byteLength !== E2EE_KEY_BYTES) throw new Error('The E2EE key PEM does not contain a 256-bit key.');
  const key: E2eeKeyRecord = {
    id: pemHeaderValue(headers, 'Key-Id') ?? '',
    name: pemHeaderValue(headers, 'Name') ?? fallbackName,
    a: (pemHeaderValue(headers, 'Algorithm') ?? E2EE_ALG) as E2eeAlgorithm,
    k: base64UrlEncode(keyBytes),
    createdAt: pemHeaderValue(headers, 'Created-At') ?? new Date().toISOString(),
  };
  if (!isE2eeKeyRecord(key)) throw new Error('The E2EE key PEM has invalid metadata.');
  return {
    ...key,
    name: key.name.trim() || fallbackName,
  };
}

export function parseE2eeKeyRecord(raw: string, fallbackName: string): E2eeKeyRecord {
  return parseE2eeKeyRecordPem(raw.trim(), fallbackName);
}

export function serializeE2eeKeyRecord(key: E2eeKeyRecord): string {
  return [
    `-----BEGIN ${E2EE_PEM_LABEL}-----`,
    `Key-Id: ${key.id}`,
    `Name: ${key.name}`,
    `Algorithm: ${key.a}`,
    `Created-At: ${key.createdAt}`,
    '',
    encodePemBase64(base64UrlDecode(key.k)),
    `-----END ${E2EE_PEM_LABEL}-----`,
    '',
  ].join('\n');
}
